import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, Plus, Trash2, Save, Loader2, Copy, Filter, Calendar,
  CheckCircle2, AlertTriangle, Layers, Clock, FileText, Settings, UserCheck
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODULES = [
  { id: 'all', label: 'All Modules (Master Policy)' },
  { id: 'leave', label: 'Leave Policy' },
  { id: 'permission', label: 'Permission' },
  { id: 'attendance', label: 'Attendance Rules' },
  { id: 'regularization', label: 'Regularization Rules' },
  { id: 'shift', label: 'Shift Change Rules' },
  { id: 'compoff', label: 'Comp-Off Rules' },
];

const LEAVE_TYPES = ['CL', 'SL', 'EL', 'PL', 'ML', 'LOP', 'CompOff', 'Other'];

const emptyPolicy = {
  policy_name: '',
  company_id: '',
  branch_id: '',
  department_id: '',
  division_id: '',
  employee_category_id: '',
  designation_id: '',
  applicable_employee_id: '',
  module: 'all',
  priority: 50,
  effective_from: new Date().toISOString().split('T')[0],
  effective_to: '',
  is_active: true,
  notes: '',
  leave_rules: [
    {
      leave_type: 'CL',
      min_advance_notice_days: 1,
      max_backdated_days: 3,
      half_day_allowed: true,
      attachment_required: false,
      special_approval_after_deadline: true,
      is_active: true
    },
    {
      leave_type: 'SL',
      min_advance_notice_days: 0,
      max_backdated_days: 7,
      half_day_allowed: true,
      attachment_required: false,
      medical_cert_required: true,
      special_approval_after_deadline: true,
      is_active: true
    },
    {
      leave_type: 'EL',
      min_advance_notice_days: 3,
      max_backdated_days: 2,
      half_day_allowed: false,
      attachment_required: false,
      special_approval_after_deadline: true,
      is_active: true
    }
  ],
  permission_rule: {
    monthly_permission_limit: 2,
    max_duration_minutes: 60,
    min_duration_minutes: 15,
    same_day_permission_allowed: true,
    backdated_permission_allowed: true,
    max_backdated_days: 3,
    special_approval_after_limit: true,
    is_active: true
  },
  attendance_rule: {
    shift_start_time: '09:00',
    shift_end_time: '18:00',
    grace_period_minutes: 15,
    late_arrival_threshold_minutes: 30,
    early_departure_threshold_minutes: 30,
    half_day_threshold_hours: 4.0,
    full_day_threshold_hours: 8.0,
    regularization_allowed: true,
    max_regularization_attempts: 3,
    max_backdated_regularization_days: 5,
    special_approval_required: true,
    is_active: true
  },
  regularization_rule: {
    regularization_allowed: true,
    allowed_types: ['missing_punch', 'late_arrival', 'early_exit', 'wrong_shift', 'absent_correction'],
    application_deadline_days: 5,
    max_backdated_days: 30,
    monthly_request_limit: 5,
    special_approval_after_deadline: true,
    is_active: true
  },
  shift_rule: {
    shift_change_allowed: true,
    min_advance_notice_days: 1,
    max_backdated_days: 0,
    max_future_days: 30,
    special_approval_after_deadline: true,
    is_active: true
  },
  compoff_rule: {
    compoff_eligibility: true,
    eligible_workdays: true,
    eligible_holidays: true,
    min_extra_working_hours: 4.0,
    max_compoff_days: 12.0,
    validity_period_days: 90,
    max_backdated_days: 7,
    special_approval_after_deadline: true,
    is_active: true
  }
};

export default function UnifiedPolicyManagement() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [selectedModule, setSelectedModule] = useState('all');
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
        const cs = Array.isArray(data) ? data : (data.companies || []);
        setCompanies(cs);
        if (cs.length > 0) setCompanyId(cs[0].id);
      } catch { /* ignore */ }
    })();
  }, []);

  const loadPolicies = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      let url = `${API_URL}/unified-policy/policies?include_inactive=true`;
      if (companyId) url += `&company_id=${companyId}`;
      if (selectedModule !== 'all') url += `&module=${selectedModule}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setPolicies(data.policies || []);
    } catch (e) {
      const errMsg = e.response?.data?.detail || 'Unable to load policies. Please check connection and try again.';
      setFetchError(errMsg);
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  }, [companyId, selectedModule]);

  useEffect(() => {
    loadPolicies();
  }, [companyId, selectedModule, loadPolicies]);

  const handleSavePolicy = async (policyData) => {
    try {
      const { data } = await axios.post(`${API_URL}/unified-policy/policies`, policyData, { withCredentials: true });
      toast.success(data.action === 'created' ? 'Policy created successfully' : 'Policy updated successfully');
      setEditingPolicy(null);
      loadPolicies();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleDeletePolicy = async (policyId, policyName) => {
    if (!window.confirm(`Deactivate/Soft Delete policy '${policyName}'?`)) return;
    try {
      await axios.delete(`${API_URL}/unified-policy/policies/${policyId}`, { withCredentials: true });
      toast.success('Policy deactivated and archived');
      loadPolicies();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleDuplicatePolicy = async (policyId) => {
    try {
      const { data } = await axios.post(`${API_URL}/unified-policy/policies/${policyId}/duplicate`, {}, { withCredentials: true });
      toast.success(`Duplicated as '${data.policy_name}'`);
      loadPolicies();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Duplicate failed');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="unified-policy-management-page">
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="text-indigo-600" size={28} /> Unified Leave & Attendance Policy Master
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Centralized policy controls across Leave, Permission, Attendance, Regularization, Shift, and Comp-Off with rule precedence hierarchy.
          </p>
        </div>
        <button
          onClick={() => {
            setEditingPolicy({ ...emptyPolicy, company_id: companyId });
            setActiveTab('basic');
          }}
          className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2 rounded-xl text-sm shadow-md transition-all"
        >
          <Plus size={18} /> Create Unified Policy
        </button>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-6 flex flex-wrap items-center gap-4 shadow-sm">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Company:</span>
          <select
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">— All Companies —</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Module Scope:</span>
          <select
            value={selectedModule}
            onChange={(e) => setSelectedModule(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {MODULES.map((m) => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Policy List */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-12 text-center text-slate-500">
            <Loader2 className="animate-spin inline mr-2 text-indigo-600" size={24} /> Loading Policy Engine Rules...
          </div>
        ) : fetchError ? (
          <div className="p-12 text-center text-slate-500">
            <AlertTriangle className="inline text-rose-500 mb-2" size={32} />
            <p className="font-semibold text-rose-700 mb-2">{fetchError}</p>
            <button
              onClick={loadPolicies}
              className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2 rounded-xl text-sm shadow-md transition-all"
            >
              Retry
            </button>
          </div>
        ) : policies.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <AlertTriangle className="inline text-amber-500 mb-2" size={32} />
            <p className="font-semibold text-slate-700">No active policies found for selected criteria.</p>
            <p className="text-xs text-slate-400 mt-1">Default System Policy applies automatically until a custom policy is defined.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-slate-600 text-xs font-semibold uppercase border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3">Priority / Precedence</th>
                  <th className="px-4 py-3">Policy Name</th>
                  <th className="px-4 py-3">Scope & Target</th>
                  <th className="px-4 py-3">Module</th>
                  <th className="px-4 py-3">Effective Range</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {policies.map((pol) => {
                  const isEmployeeSpecific = !!pol.applicable_employee_id;
                  const isDeptSpecific = !!pol.department_id;
                  const isBranchSpecific = !!pol.branch_id;
                  const isDesigSpecific = !!pol.designation_id;

                  let scopeBadge = "Company Default";
                  let scopeDetail = pol.company_name || "All Companies";

                  if (isEmployeeSpecific) {
                    scopeBadge = "Employee Override";
                    scopeDetail = pol.applicable_employee_name || pol.applicable_employee_id;
                  } else if (isDeptSpecific) {
                    scopeBadge = "Department Level";
                    scopeDetail = pol.department_name || pol.department_id;
                  } else if (isBranchSpecific) {
                    scopeBadge = "Branch Level";
                    scopeDetail = pol.branch_name || pol.branch_id;
                  } else if (isDesigSpecific) {
                    scopeBadge = "Designation Level";
                    scopeDetail = pol.designation_name || pol.designation_id;
                  }

                  return (
                    <tr key={pol.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 font-mono text-xs font-bold px-2.5 py-1 rounded-md bg-slate-100 text-slate-700 border border-slate-200">
                          Priority #{pol.priority || 50}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-bold text-slate-900">
                        {pol.policy_name}
                        <div className="text-xs font-normal text-slate-400">v{pol.version || 1} • {pol.company_name || 'Company'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          isEmployeeSpecific ? 'bg-purple-50 text-purple-700 border border-purple-200' :
                          isDeptSpecific ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                          isBranchSpecific ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                          isDesigSpecific ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {scopeBadge}
                        </span>
                        <div className="text-xs text-slate-500 font-medium mt-0.5">{scopeDetail}</div>
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-700 uppercase text-xs">
                        {pol.module || 'all'}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600 font-mono">
                        {pol.effective_from || '—'} to {pol.effective_to || 'Indefinite'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                          pol.is_active ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500'
                        }`}>
                          {pol.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => { setEditingPolicy(pol); setActiveTab('basic'); }}
                          className="text-indigo-600 hover:underline text-xs font-semibold mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDuplicatePolicy(pol.id)}
                          className="text-emerald-600 hover:underline text-xs font-semibold mr-3"
                        >
                          Clone
                        </button>
                        <button
                          onClick={() => handleDeletePolicy(pol.id, pol.policy_name)}
                          className="text-rose-600 hover:underline text-xs font-semibold"
                        >
                          Deactivate
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Policy Modal Editor */}
      {editingPolicy && (
        <PolicyModalEditor
          policy={editingPolicy}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          companies={companies}
          onClose={() => setEditingPolicy(null)}
          onSave={handleSavePolicy}
        />
      )}
    </div>
  );
}

function PolicyModalEditor({ policy, activeTab, setActiveTab, companies, onClose, onSave }) {
  const [p, setP] = useState(policy);
  const [masterOptions, setMasterOptions] = useState({ branches: [], departments: [], designations: [], employees: [] });
  const [loadingOptions, setLoadingOptions] = useState(false);

  const setField = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    if (p.company_id) {
      setLoadingOptions(true);
      axios.get(`${API_URL}/unified-policy/master-options?company_id=${p.company_id}`, { withCredentials: true })
        .then(({ data }) => setMasterOptions(data || { branches: [], departments: [], designations: [], employees: [] }))
        .catch(() => {})
        .finally(() => setLoadingOptions(false));
    }
  }, [p.company_id]);

  const handleValidationAndSave = () => {
    if (!p.policy_name || !p.policy_name.trim()) {
      toast.error('Policy Name is required.');
      setActiveTab('basic');
      return;
    }
    if (!p.company_id) {
      toast.error('Company selection is required.');
      setActiveTab('basic');
      return;
    }
    if (!p.effective_from) {
      toast.error('Effective From Date is required.');
      setActiveTab('basic');
      return;
    }
    if (p.effective_to && p.effective_to < p.effective_from) {
      toast.error('Effective To Date cannot be earlier than Effective From Date.');
      setActiveTab('basic');
      return;
    }
    onSave(p);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-slate-900 text-lg">
              {p.id ? `Edit Policy Master (${p.policy_name})` : 'Create Unified Policy Master'}
            </h2>
            <p className="text-xs text-slate-500">Configure scope, rules, deadlines, and special approval flags.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-sm font-bold">✕</button>
        </div>

        {/* Modal Tabs */}
        <div className="border-b border-slate-200 bg-white px-6 flex items-center gap-2 overflow-x-auto text-xs font-semibold">
          {[
            { id: 'basic', label: 'Basic & Scope' },
            { id: 'leave', label: 'Leave Rules' },
            { id: 'permission', label: 'Permission' },
            { id: 'attendance', label: 'Attendance Rules' },
            { id: 'regularization', label: 'Regularization' },
            { id: 'shift', label: 'Shift Change' },
            { id: 'compoff', label: 'Comp-Off' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-3 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-indigo-600 text-indigo-700 font-bold'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-4 text-sm">
          {activeTab === 'basic' && (
            <div className="grid grid-cols-2 gap-4">
              <FormGroup label="Policy Name *">
                <input
                  type="text"
                  value={p.policy_name}
                  onChange={(e) => setField('policy_name', e.target.value)}
                  className="input font-bold"
                  placeholder="e.g. Standard Company Leave & Permission Policy"
                />
              </FormGroup>

              <FormGroup label="Company *">
                <select
                  value={p.company_id}
                  onChange={(e) => setField('company_id', e.target.value)}
                  className="input font-medium"
                >
                  <option value="">— Select Company —</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </FormGroup>

              <FormGroup label="Module Scope">
                <select
                  value={p.module || 'all'}
                  onChange={(e) => setField('module', e.target.value)}
                  className="input font-medium"
                >
                  {MODULES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </FormGroup>

              <FormGroup label="Precedence Priority (Lower Number = Higher Precedence)">
                <input
                  type="number"
                  value={p.priority || 50}
                  onChange={(e) => setField('priority', parseInt(e.target.value || 50))}
                  className="input font-mono"
                  placeholder="10=Employee, 20=Desig, 30=Dept, 40=Branch, 50=Company"
                />
              </FormGroup>

              <FormGroup label="Effective From Date *">
                <input
                  type="date"
                  value={p.effective_from || ''}
                  onChange={(e) => setField('effective_from', e.target.value)}
                  className="input"
                />
              </FormGroup>

              <FormGroup label="Effective To Date (Leave blank for perpetual)">
                <input
                  type="date"
                  value={p.effective_to || ''}
                  onChange={(e) => setField('effective_to', e.target.value)}
                  className="input"
                />
              </FormGroup>

              <FormGroup label="Branch / Location Filter (Optional)">
                <select
                  value={p.branch_id || ''}
                  onChange={(e) => setField('branch_id', e.target.value)}
                  className="input font-medium"
                >
                  <option value="">— All Branches / Locations —</option>
                  {masterOptions.branches.map((b) => (
                    <option key={b.id} value={b.id}>{b.name || b.branch_name || b.code}</option>
                  ))}
                </select>
              </FormGroup>

              <FormGroup label="Department Filter (Optional)">
                <select
                  value={p.department_id || ''}
                  onChange={(e) => setField('department_id', e.target.value)}
                  className="input font-medium"
                >
                  <option value="">— All Departments —</option>
                  {masterOptions.departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name || d.department_name || d.code}</option>
                  ))}
                </select>
              </FormGroup>

              <FormGroup label="Designation Filter (Optional)">
                <select
                  value={p.designation_id || ''}
                  onChange={(e) => setField('designation_id', e.target.value)}
                  className="input font-medium"
                >
                  <option value="">— All Designations —</option>
                  {masterOptions.designations.map((ds) => (
                    <option key={ds.id} value={ds.id}>{ds.name || ds.designation_name || ds.code}</option>
                  ))}
                </select>
              </FormGroup>

              <FormGroup label="Employee Specific Override (Optional 1-to-1 Override)">
                <select
                  value={p.applicable_employee_id || ''}
                  onChange={(e) => setField('applicable_employee_id', e.target.value)}
                  className="input font-medium"
                >
                  <option value="">— No Employee Override (Group Policy) —</option>
                  {masterOptions.employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>{emp.label}</option>
                  ))}
                </select>
              </FormGroup>

              <div className="col-span-2 pt-2 border-t border-slate-100 flex items-center gap-3">
                <label className="flex items-center gap-2 font-semibold text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!p.is_active}
                    onChange={(e) => setField('is_active', e.target.checked)}
                    className="rounded text-indigo-600"
                  />
                  Policy is Active
                </label>
              </div>
            </div>
          )}

          {activeTab === 'leave' && (
            <div className="space-y-4">
              <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-xl text-xs text-indigo-800">
                Configure advance notice requirements, backdated deadlines, and special approval flags per leave type.
              </div>
              {(p.leave_rules || []).map((lr, idx) => (
                <div key={idx} className="p-4 border border-slate-200 rounded-xl bg-slate-50/50 space-y-3 text-xs">
                  <div className="flex items-center justify-between font-bold text-indigo-800 text-sm">
                    <span>Leave Type: {lr.leave_type}</span>
                    <label className="flex items-center gap-1.5 font-normal text-slate-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!lr.is_active}
                        onChange={(e) => {
                          const updated = [...p.leave_rules];
                          updated[idx].is_active = e.target.checked;
                          setField('leave_rules', updated);
                        }}
                      />
                      Active
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FormGroup label="Min Advance Notice (Days)">
                      <input
                        type="number"
                        value={lr.min_advance_notice_days ?? ''}
                        onChange={(e) => {
                          const updated = [...p.leave_rules];
                          updated[idx].min_advance_notice_days = e.target.value === '' ? '' : parseInt(e.target.value);
                          setField('leave_rules', updated);
                        }}
                        className="input"
                      />
                    </FormGroup>
                    <FormGroup label="Max Backdated Application (Days)">
                      <input
                        type="number"
                        value={lr.max_backdated_days ?? ''}
                        onChange={(e) => {
                          const updated = [...p.leave_rules];
                          updated[idx].max_backdated_days = e.target.value === '' ? '' : parseInt(e.target.value);
                          setField('leave_rules', updated);
                        }}
                        className="input"
                      />
                    </FormGroup>
                  </div>

                  <div className="flex items-center gap-4 pt-2">
                    <label className="flex items-center gap-1.5 cursor-pointer font-semibold text-slate-700">
                      <input
                        type="checkbox"
                        checked={!!lr.special_approval_after_deadline}
                        onChange={(e) => {
                          const updated = [...p.leave_rules];
                          updated[idx].special_approval_after_deadline = e.target.checked;
                          setField('leave_rules', updated);
                        }}
                      />
                      Enable Special Approval workflow if deadline/notice rule is exceeded
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'permission' && (
            <div className="grid grid-cols-2 gap-4">
              <FormGroup label="Monthly Permission Limit (Count)">
                <input
                  type="number"
                  value={p.permission_rule?.monthly_permission_limit ?? 1}
                  onChange={(e) =>
                    setField('permission_rule', {
                      ...p.permission_rule,
                      monthly_permission_limit: e.target.value === '' ? '' : parseInt(e.target.value),
                    })
                  }
                  className="input font-bold text-indigo-700"
                />
              </FormGroup>

              <FormGroup label="Max Duration Per Request (Minutes)">
                <input
                  type="number"
                  value={p.permission_rule?.max_duration_minutes ?? ''}
                  onChange={(e) =>
                    setField('permission_rule', {
                      ...p.permission_rule,
                      max_duration_minutes: e.target.value === '' ? '' : parseInt(e.target.value),
                    })
                  }
                  className="input"
                />
              </FormGroup>

              <FormGroup label="Max Backdated Application (Days)">
                <input
                  type="number"
                  value={p.permission_rule?.max_backdated_days ?? ''}
                  onChange={(e) =>
                    setField('permission_rule', {
                      ...p.permission_rule,
                      max_backdated_days: e.target.value === '' ? '' : parseInt(e.target.value),
                    })
                  }
                  className="input"
                />
              </FormGroup>

              <div className="col-span-2 pt-2 border-t border-slate-100">
                <label className="flex items-center gap-2 font-semibold text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!p.permission_rule?.special_approval_after_limit}
                    onChange={(e) =>
                      setField('permission_rule', {
                        ...p.permission_rule,
                        special_approval_after_limit: e.target.checked,
                      })
                    }
                    className="rounded text-indigo-600"
                  />
                  Enable Special Approval when monthly limit or backdated deadline is exceeded
                </label>
              </div>
            </div>
          )}

          {activeTab === 'attendance' && (
            <div className="grid grid-cols-2 gap-4">
              <FormGroup label="Grace Period (Minutes)">
                <input
                  type="number"
                  value={p.attendance_rule?.grace_period_minutes ?? ''}
                  onChange={(e) =>
                    setField('attendance_rule', {
                      ...p.attendance_rule,
                      grace_period_minutes: e.target.value === '' ? '' : parseInt(e.target.value),
                    })
                  }
                  className="input"
                />
              </FormGroup>

              <FormGroup label="Late Arrival Threshold (Minutes)">
                <input
                  type="number"
                  value={p.attendance_rule?.late_arrival_threshold_minutes ?? ''}
                  onChange={(e) =>
                    setField('attendance_rule', {
                      ...p.attendance_rule,
                      late_arrival_threshold_minutes: e.target.value === '' ? '' : parseInt(e.target.value),
                    })
                  }
                  className="input"
                />
              </FormGroup>

              <FormGroup label="Half Day Threshold (Hours)">
                <input
                  type="number"
                  step="0.5"
                  value={p.attendance_rule?.half_day_threshold_hours ?? ''}
                  onChange={(e) =>
                    setField('attendance_rule', {
                      ...p.attendance_rule,
                      half_day_threshold_hours: e.target.value === '' ? '' : parseFloat(e.target.value),
                    })
                  }
                  className="input"
                />
              </FormGroup>

              <FormGroup label="Full Day Threshold (Hours)">
                <input
                  type="number"
                  step="0.5"
                  value={p.attendance_rule?.full_day_threshold_hours ?? ''}
                  onChange={(e) =>
                    setField('attendance_rule', {
                      ...p.attendance_rule,
                      full_day_threshold_hours: e.target.value === '' ? '' : parseFloat(e.target.value),
                    })
                  }
                  className="input"
                />
              </FormGroup>
            </div>
          )}

          {activeTab === 'regularization' && (
            <div className="grid grid-cols-2 gap-4">
              <FormGroup label="Max Backdated Regularization (Days)">
                <input
                  type="number"
                  value={p.regularization_rule?.max_backdated_days ?? ''}
                  onChange={(e) =>
                    setField('regularization_rule', {
                      ...p.regularization_rule,
                      max_backdated_days: e.target.value === '' ? '' : parseInt(e.target.value),
                    })
                  }
                  className="input font-bold"
                />
              </FormGroup>

              <FormGroup label="Monthly Request Limit">
                <input
                  type="number"
                  value={p.regularization_rule?.monthly_request_limit ?? ''}
                  onChange={(e) =>
                    setField('regularization_rule', {
                      ...p.regularization_rule,
                      monthly_request_limit: e.target.value === '' ? '' : parseInt(e.target.value),
                    })
                  }
                  className="input"
                />
              </FormGroup>

              <div className="col-span-2 pt-2 border-t border-slate-100">
                <label className="flex items-center gap-2 font-semibold text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!p.regularization_rule?.special_approval_after_deadline}
                    onChange={(e) =>
                      setField('regularization_rule', {
                        ...p.regularization_rule,
                        special_approval_after_deadline: e.target.checked,
                      })
                    }
                    className="rounded text-indigo-600"
                  />
                  Allow Special Approval for backdated regularizations past deadline
                </label>
              </div>
            </div>
          )}

          {activeTab === 'shift' && (
            <div className="grid grid-cols-2 gap-4">
              <FormGroup label="Min Advance Notice (Days)">
                <input
                  type="number"
                  value={p.shift_rule?.min_advance_notice_days ?? ''}
                  onChange={(e) =>
                    setField('shift_rule', {
                      ...p.shift_rule,
                      min_advance_notice_days: e.target.value === '' ? '' : parseInt(e.target.value),
                    })
                  }
                  className="input"
                />
              </FormGroup>

              <FormGroup label="Max Future Days Allowed">
                <input
                  type="number"
                  value={p.shift_rule?.max_future_days ?? ''}
                  onChange={(e) =>
                    setField('shift_rule', {
                      ...p.shift_rule,
                      max_future_days: e.target.value === '' ? '' : parseInt(e.target.value),
                    })
                  }
                  className="input"
                />
              </FormGroup>
            </div>
          )}

          {activeTab === 'compoff' && (
            <div className="space-y-6">
              {/* Header & Enable Toggle */}
              <div className="flex items-center justify-between p-4 bg-indigo-50/60 border border-indigo-100 rounded-xl">
                <div>
                  <h4 className="font-bold text-slate-800 text-sm">Comp-Off Policy Configuration</h4>
                  <p className="text-xs text-slate-500">Configure hours to comp-off days conversion rules, validity, and eligibility thresholds.</p>
                </div>
                <label className="flex items-center gap-2 font-bold text-sm text-indigo-900 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={p.compoff_rule?.enable_compoff !== false && p.compoff_rule?.compoff_eligibility !== false}
                    onChange={(e) =>
                      setField('compoff_rule', {
                        ...p.compoff_rule,
                        enable_compoff: e.target.checked,
                        compoff_eligibility: e.target.checked,
                      })
                    }
                    className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                  />
                  Enable Comp-Off Policy
                </label>
              </div>

              {/* Conversion Rules Builder */}
              <div className="border border-slate-200 rounded-xl p-4 space-y-3 bg-white">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Conversion Rules (Hours Worked → Comp-Off Credit)</span>
                  <button
                    type="button"
                    onClick={() => {
                      const curRules = p.compoff_rule?.conversion_rules || [
                        { hours: 4.0, comp_off_days: 0.5 },
                        { hours: 8.0, comp_off_days: 1.0 },
                      ];
                      const lastH = curRules.length > 0 ? (curRules[curRules.length - 1].hours + 4) : 4;
                      const lastD = curRules.length > 0 ? (curRules[curRules.length - 1].comp_off_days + 0.5) : 0.5;
                      setField('compoff_rule', {
                        ...p.compoff_rule,
                        conversion_rules: [...curRules, { hours: lastH, comp_off_days: lastD }],
                      });
                    }}
                    className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg border border-indigo-200 transition-colors"
                  >
                    <Plus size={14} /> Add Conversion Rule
                  </button>
                </div>

                <div className="overflow-x-auto border border-slate-100 rounded-lg">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-slate-50 text-slate-600 uppercase font-semibold text-[10px]">
                      <tr>
                        <th className="px-3 py-2.5">Hours Worked</th>
                        <th className="px-3 py-2.5">Comp-Off Credit (Days)</th>
                        <th className="px-3 py-2.5 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(p.compoff_rule?.conversion_rules || [
                        { hours: 4.0, comp_off_days: 0.5 },
                        { hours: 8.0, comp_off_days: 1.0 },
                        { hours: 12.0, comp_off_days: 1.5 },
                        { hours: 16.0, comp_off_days: 2.0 },
                      ]).map((rule, rIdx) => (
                        <tr key={rIdx} className="hover:bg-slate-50/50">
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1.5">
                              <input
                                type="number"
                                step="0.5"
                                value={rule.hours}
                                onChange={(e) => {
                                  const updated = [...(p.compoff_rule?.conversion_rules || [
                                    { hours: 4.0, comp_off_days: 0.5 },
                                    { hours: 8.0, comp_off_days: 1.0 },
                                    { hours: 12.0, comp_off_days: 1.5 },
                                    { hours: 16.0, comp_off_days: 2.0 },
                                  ])];
                                  updated[rIdx] = { ...updated[rIdx], hours: parseFloat(e.target.value || 0) };
                                  setField('compoff_rule', { ...p.compoff_rule, conversion_rules: updated });
                                }}
                                className="w-24 border border-slate-300 rounded px-2 py-1 font-mono text-xs"
                              />
                              <span className="text-slate-500 font-medium">Hours</span>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1.5">
                              <input
                                type="number"
                                step="0.25"
                                value={rule.comp_off_days}
                                onChange={(e) => {
                                  const updated = [...(p.compoff_rule?.conversion_rules || [
                                    { hours: 4.0, comp_off_days: 0.5 },
                                    { hours: 8.0, comp_off_days: 1.0 },
                                    { hours: 12.0, comp_off_days: 1.5 },
                                    { hours: 16.0, comp_off_days: 2.0 },
                                  ])];
                                  updated[rIdx] = { ...updated[rIdx], comp_off_days: parseFloat(e.target.value || 0) };
                                  setField('compoff_rule', { ...p.compoff_rule, conversion_rules: updated });
                                }}
                                className="w-24 border border-slate-300 rounded px-2 py-1 font-mono text-xs font-bold text-indigo-700"
                              />
                              <span className="text-slate-500 font-medium">Day(s)</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => {
                                const updated = (p.compoff_rule?.conversion_rules || [
                                  { hours: 4.0, comp_off_days: 0.5 },
                                  { hours: 8.0, comp_off_days: 1.0 },
                                  { hours: 12.0, comp_off_days: 1.5 },
                                  { hours: 16.0, comp_off_days: 2.0 },
                                ]).filter((_, i) => i !== rIdx);
                                setField('compoff_rule', { ...p.compoff_rule, conversion_rules: updated });
                              }}
                              className="text-rose-600 hover:text-rose-800 text-xs font-semibold"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Policy Parameters Grid */}
              <div className="grid grid-cols-2 gap-4">
                <FormGroup label="Minimum Extra Working Hours Required">
                  <input
                    type="number"
                    step="0.5"
                    value={p.compoff_rule?.min_extra_working_hours ?? ''}
                    onChange={(e) =>
                      setField('compoff_rule', {
                        ...p.compoff_rule,
                        min_extra_working_hours: e.target.value === '' ? '' : parseFloat(e.target.value),
                      })
                    }
                    className="input font-medium"
                  />
                </FormGroup>

                <FormGroup label="Maximum Comp-Off Days Allowed (Cap)">
                  <input
                    type="number"
                    step="0.5"
                    value={p.compoff_rule?.max_compoff_days ?? ''}
                    onChange={(e) =>
                      setField('compoff_rule', {
                        ...p.compoff_rule,
                        max_compoff_days: e.target.value === '' ? '' : parseFloat(e.target.value),
                      })
                    }
                    className="input font-medium"
                  />
                </FormGroup>

                <FormGroup label="Comp-Off Validity Period (Days from Worked Date)">
                  <input
                    type="number"
                    value={p.compoff_rule?.validity_period_days ?? ''}
                    onChange={(e) =>
                      setField('compoff_rule', {
                        ...p.compoff_rule,
                        validity_period_days: e.target.value === '' ? '' : parseInt(e.target.value),
                      })
                    }
                    className="input font-bold text-indigo-700"
                  />
                </FormGroup>

                <FormGroup label="Max Backdated Application Deadline (Days)">
                  <input
                    type="number"
                    value={p.compoff_rule?.max_backdated_days ?? ''}
                    onChange={(e) =>
                      setField('compoff_rule', {
                        ...p.compoff_rule,
                        max_backdated_days: e.target.value === '' ? '' : parseInt(e.target.value),
                      })
                    }
                    className="input font-medium"
                  />
                </FormGroup>

                <FormGroup label="Minimum Application Notice (Days)">
                  <input
                    type="number"
                    value={p.compoff_rule?.min_application_notice_days ?? ''}
                    onChange={(e) =>
                      setField('compoff_rule', {
                        ...p.compoff_rule,
                        min_application_notice_days: e.target.value === '' ? '' : parseInt(e.target.value),
                      })
                    }
                    className="input font-medium"
                  />
                </FormGroup>

                <div className="flex items-center gap-4 pt-6">
                  <label className="flex items-center gap-2 font-semibold text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={p.compoff_rule?.allow_partial_day !== false}
                      onChange={(e) =>
                        setField('compoff_rule', {
                          ...p.compoff_rule,
                          allow_partial_day: e.target.checked,
                        })
                      }
                      className="rounded text-indigo-600"
                    />
                    Allow Partial-Day Comp-Off
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm font-semibold text-slate-700"
          >
            Cancel
          </button>
          <button
            onClick={handleValidationAndSave}
            className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold flex items-center gap-1.5 shadow-md"
          >
            <Save size={16} /> Save Policy Engine Master
          </button>
        </div>
      </div>
      <style>{`
        .input { width: 100%; border: 1px solid #cbd5e1; border-radius: 0.5rem; padding: 0.5rem 0.75rem; font-size: 0.875rem; }
      `}</style>
    </div>
  );
}

function FormGroup({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-700">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
